import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { contentApi } from '../../services/contentApi';
import { Category } from '../../types/content';
import Button from '../../components/UI/Button';
import ActionStatus from '../../components/UI/ActionStatus';
import Input from '../../components/UI/Input';
import Card from '../../components/UI/Card';
import { canWriteGigs, submitMessage } from '../../utils/roles';
import { useAuth } from '../../context/AuthContext';
import { UnsavedLeaveDialog, useDirtyBaseline, useUnsavedChangesGuard } from '../../components/content/UnsavedLeaveDialog';
import { useAutoClear } from '../../hooks/useAutoClear';

const GigEditorPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const gigId = id ? Number(id) : undefined;
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [summary, setSummary] = useState('');
  const [clientName, setClientName] = useState('');
  const [duration, setDuration] = useState('');
  const [weeklyHours, setWeeklyHours] = useState('');
  const [techStack, setTechStack] = useState('');
  const [priceType, setPriceType] = useState('Hourly');
  const [priceAmount, setPriceAmount] = useState('');
  const [currency, setCurrency] = useState('INR');
  const [jobDescription, setJobDescription] = useState('');
  const [whoCanApply, setWhoCanApply] = useState('');
  const [teamType, setTeamType] = useState('Individual');
  const [applyUrl, setApplyUrl] = useState('');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useAutoClear(message, setMessage, 5000);
  useAutoClear(error, setError, 10000);
  const [loading, setLoading] = useState(!!gigId);

  useEffect(() => {
    contentApi.listCategories('Gig').then((r) => setCategories(r.data.categories || [])).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!gigId) return;
    setLoading(true);
    contentApi.getGig(gigId).then((r) => {
      const gig = r.data.gig;
      setTitle(gig.title);
      setSubtitle(gig.subtitle || '');
      setSummary(gig.summary || '');
      setClientName(gig.clientName);
      setDuration(gig.duration || '');
      setWeeklyHours(gig.weeklyHours || '');
      setTechStack(gig.techStack || '');
      setPriceType(gig.priceType || 'Hourly');
      setPriceAmount(gig.priceAmount != null ? String(gig.priceAmount) : '');
      setCurrency(gig.currency || 'INR');
      setJobDescription(gig.jobDescription || '');
      setWhoCanApply(gig.whoCanApply || '');
      setTeamType(gig.teamType || 'Individual');
      setApplyUrl(gig.applyUrl || '');
      setCategoryId(gig.categoryId || '');
    }).catch(() => setError('Unable to load this gig.')).finally(() => setLoading(false));
  }, [gigId]);

  const payload = useMemo(() => ({
    title,
    subtitle,
    summary,
    clientName,
    duration,
    weeklyHours,
    techStack,
    priceType,
    priceAmount: priceAmount === '' ? null : Number(priceAmount),
    currency,
    jobDescription,
    whoCanApply,
    teamType,
    applyUrl,
    categoryId: categoryId === '' ? null : categoryId,
  }), [title, subtitle, summary, clientName, duration, weeklyHours, techStack, priceType, priceAmount, currency, jobDescription, whoCanApply, teamType, applyUrl, categoryId]);

  const { isDirty, markClean } = useDirtyBaseline(payload, !loading);
  const { leaveOpen, stay, leaveWithoutSaving } = useUnsavedChangesGuard(isDirty);

  const save = async (submit = false) => {
    setSaving(true);
    setError(null);
    try {
      const response = await contentApi.saveGig(payload, gigId);
      const savedId = response.data.gig.id;
      if (submit) {
        await contentApi.submitGig(savedId);
        setMessage(submitMessage(user));
      } else {
        setMessage('Draft saved.');
      }
      markClean();
      if (!gigId) navigate(`/admin/gigs/${savedId}/edit`, { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  if (!canWriteGigs(user)) {
    return <div className="max-w-4xl mx-auto px-4 py-10 text-gray-600">You do not have permission to create gigs.</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/admin/dashboard?tab=gigs" className="inline-flex items-center text-sm font-medium text-primary-700 hover:text-primary-800">← Back to gigs</Link>
          <h1 className="mt-2 text-3xl font-bold text-gray-900">{gigId ? 'Edit gig' : 'New gig'}</h1>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <div className="flex gap-2">
          <Button type="button" variant="outline" isLoading={saving} onClick={() => save(false)}>Save draft</Button>
          <Button type="button" isLoading={saving} onClick={() => save(true)}>Submit</Button>
          </div>
          <ActionStatus className="w-full max-w-md" message={message} error={error} />
        </div>
      </div>
      <p className="mt-2 text-gray-600">Paid work posted by an admin or client. Learners use Projects to practice; gigs are chances to earn.</p>
      <Card className="mt-6 p-6 space-y-4">
        <Input id="gig-title" label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
        <Input id="gig-client" label="Client name" value={clientName} onChange={(e) => setClientName(e.target.value)} />
        <Input id="gig-subtitle" label="Subtitle" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
        <label className="block text-sm font-medium text-gray-700">Summary
          <textarea className="mt-1 w-full rounded-md border-gray-300" rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} />
        </label>
        <div className="grid md:grid-cols-2 gap-4">
          <Input id="gig-duration" label="Duration" value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="8 weeks" />
          <Input id="gig-hours" label="Weekly hours" value={weeklyHours} onChange={(e) => setWeeklyHours(e.target.value)} placeholder="10–15 hours" />
        </div>
        <Input id="gig-stack" label="Tech stack" value={techStack} onChange={(e) => setTechStack(e.target.value)} placeholder=".NET, React, SQL" />
        <div className="grid md:grid-cols-3 gap-4">
          <label className="block text-sm font-medium text-gray-700">Price type
            <select className="mt-1 w-full rounded-md border-gray-300" value={priceType} onChange={(e) => setPriceType(e.target.value)}>
              <option>Hourly</option>
              <option>Total</option>
            </select>
          </label>
          <Input id="gig-price" label={priceType === 'Hourly' ? 'Price per hour' : 'Total price'} value={priceAmount} onChange={(e) => setPriceAmount(e.target.value)} />
          <Input id="gig-currency" label="Currency" value={currency} onChange={(e) => setCurrency(e.target.value)} />
        </div>
        <label className="block text-sm font-medium text-gray-700">Job description
          <textarea className="mt-1 w-full rounded-md border-gray-300" rows={8} value={jobDescription} onChange={(e) => setJobDescription(e.target.value)} />
        </label>
        <label className="block text-sm font-medium text-gray-700">Who can apply
          <textarea className="mt-1 w-full rounded-md border-gray-300" rows={4} value={whoCanApply} onChange={(e) => setWhoCanApply(e.target.value)} />
        </label>
        <div className="grid md:grid-cols-2 gap-4">
          <label className="block text-sm font-medium text-gray-700">Individual / team
            <select className="mt-1 w-full rounded-md border-gray-300" value={teamType} onChange={(e) => setTeamType(e.target.value)}>
              <option>Individual</option>
              <option>Team</option>
            </select>
          </label>
          <label className="block text-sm font-medium text-gray-700">Category
            <select className="mt-1 w-full rounded-md border-gray-300" value={categoryId} onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">None</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
        </div>
        <Input id="gig-apply" label="Apply URL" value={applyUrl} onChange={(e) => setApplyUrl(e.target.value)} />
      </Card>
      <UnsavedLeaveDialog open={leaveOpen} onStay={stay} onLeave={leaveWithoutSaving} />
    </div>
  );
};

export default GigEditorPage;
